import { ReactElement, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import dayjs, { Dayjs } from 'dayjs';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import Course from '../../types/Course';
import { CoursePair } from '../../types/Course';
import EventDay from '../../types/EventDay';
import { DisplayDate, generateFirestoreId, ToDayJS } from '../../utils/utils';
import { resolveCourseTeeTimeSpacing, resolveShotgunStartHole, resolveTeeTimeSpacing } from '../../utils/utilsEvents';
import CourseSelector from '../course/CourseSelector';

import EventDayHolesToggle from './EventDayHolesToggle';
import EventDayNumberField from './EventDayNumberField';

export interface EventTimeEditorProps {
  days: EventDay[];
  coursePairs: CoursePair[];
  defaultTime?: Dayjs;
  timezone?: string;
  isGolfEvent?: boolean;
  onChange: (days: EventDay[], coursePairs: CoursePair[]) => void;
}


function durationToDayjs(minutes: number): Dayjs {
  return dayjs('1970-01-01T00:00:00').add(minutes, 'minute');
}

function dayjsToDuration(date: Dayjs): number {
  return date.diff(dayjs('1970-01-01T00:00:00'), 'minute');
}

export default function EventTimeEditor(props: EventTimeEditorProps): ReactElement {
  const {
    days,
    coursePairs,
    defaultTime = dayjs('2000-01-01T09:00:00'),
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    isGolfEvent = true,
    onChange,
  } = props;

  
  // Remove the local state and useEffect - just use the props directly
  const updatedDays = days;
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRowExpanded = (index: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  const handleCourseChange = (index: number, course: Course) => {
    const newDays = [...updatedDays];
    newDays[index] = { ...newDays[index], courseId: course.itemId };

    // Correctly update coursePairs
    const newCoursePairs = [...coursePairs]; // Create a copy
    const existingPairIndex = newCoursePairs.findIndex((pair) => pair.id === course.itemId);

    if (existingPairIndex === -1) {
      newCoursePairs.push({
        id: course.itemId,
        name: course.name,
        shortName: course.shortName,
        timezone,
        teeTimeSpacing: course.teeTimeSpacing ?? 0,
      });
    } else {
      newCoursePairs[existingPairIndex] = {
        ...newCoursePairs[existingPairIndex],
        name: course.name,
        shortName: course.shortName,
        teeTimeSpacing: course.teeTimeSpacing ?? 0,
      };
    }

    onChange(newDays, newCoursePairs);
  };

  // Group events by date
  const groupedDays = updatedDays.reduce((groups, day, index) => {
    const date = ToDayJS(day.datetime as Timestamp).format('YYYY-MM-DD');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push({ day, index });
    return groups;
  }, {} as Record<string, Array<{ day: EventDay; index: number }>>);

  // Sort dates
  const sortedDates = Object.keys(groupedDays).sort();

  const handleAddEventToDate = (date: string) => {
    const existingEvents = groupedDays[date];
    const lastEvent = existingEvents[existingEvents.length - 1];
    
    let newDateTime: Dayjs;
    
    if (existingEvents.length === 1) {
      // If there's only one event, toggle between morning and afternoon
      const existingHour = ToDayJS(lastEvent.day.datetime as Timestamp, timezone).hour();
      
      if (existingHour < 12) {
        // Existing is morning, set new to afternoon (2pm)
        newDateTime = ToDayJS(lastEvent.day.datetime as Timestamp, timezone)
          .hour(14)
          .minute(0)
          .second(0);
      } else {
        // Existing is afternoon, set new to morning (9am)
        newDateTime = ToDayJS(lastEvent.day.datetime as Timestamp, timezone)
          .hour(9)
          .minute(0)
          .second(0);
      }
    } else {
      // Multiple events already exist, default to 9am
      newDateTime = ToDayJS(lastEvent.day.datetime as Timestamp, timezone)
        .hour(9)
        .minute(0)
        .second(0);
    }
    
    // Confirmed live bug: this used to spread lastEvent.day verbatim (only overriding datetime),
    // which silently carried its itemId onto the new day too - two different rounds (e.g. a
    // front-9 and a back-9 the same date) then shared one EventDay.itemId. Score entry prefers
    // matching a saved score to its day by that stable id (specifically so scores survive day-list
    // reordering - see EventScore.dayId's own doc comment), so with both days sharing one id, a
    // score saved for either round matched BOTH, and each day's editor showed the other's scores.
    // itemId must always be freshly generated for a new day, never inherited.
    //
    // scoringUnit (this day's active ONE_SCORE_FORMATS primitive), scoreStatus (draft/published),
    // and teeSheets (the legacy embedded array - still actively read by utilsEvents.ts,
    // seasonPairingUtils.ts, useSeasonPairings.ts, and EventRegBar.tsx, migration-in-progress
    // toward the org/.../teesheet subcollection, NOT dead data) are also reset for the same
    // reason: a brand-new round has no competitions, no scores, and no tee sheet of its own yet,
    // so carrying over the source round's values here silently gave the new round the OLD round's
    // identity in every one of those readers too, not just the ones checked first.
    const newEvent: EventDay = {
      ...lastEvent.day,
      itemId: generateFirestoreId(),
      datetime: Timestamp.fromDate(newDateTime.toDate()),
      scoringUnit: undefined,
      scoreStatus: undefined,
      teeSheets: undefined,
    };
    const newDays = [...updatedDays, newEvent];
    // Clear expanded rows to prevent newly added event from appearing expanded
    setExpandedRows(new Set());
    onChange(newDays, coursePairs);
  };

  const handleDeleteEventDay = (index: number) => {
    const newDays = updatedDays.filter((_, i) => i !== index);
    onChange(newDays, coursePairs);
    setConfirmDeleteIndex(null);
  };

  // Cheap mitigation, not a fix: removing this day shifts every day after it down one position,
  // and EventScore/EventResult/EventPayout/TeeSheet all still address a day by raw position, with
  // no remapping (unlike Competition.dayIndices, which Phase 19 does remap via each day's stable
  // itemId). Checked from `index` onward (inclusive), not just this one day, since a day AFTER the
  // one being removed is exactly what silently inherits the wrong index once the array shifts.
  const hasDownstreamScores = (index: number) => updatedDays.slice(index).some((d) => !!d.scoreStatus);

  const handleCancelDelete = () => {
    setConfirmDeleteIndex(null);
  };

  if (updatedDays.length === 0) {
    return (
      <Stack>
        <Typography variant="bodyLarge">Start time</Typography>
        <TimePicker value={defaultTime} />
      </Stack>
    );
  } else {
    return (
      <Stack spacing={2}>
        {sortedDates.map((date) => {
          const eventsOnDate = groupedDays[date];
          return (
            <Box
              key={date}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Stack
                direction={isLargeScreen ? 'row' : 'column'}
                spacing={2}
                sx={{
                  alignItems: 'stretch',
                  justifyContent: isLargeScreen ? 'space-between' : 'flex-start',
                  width: '100%'
                }}>
                {/* Date label - left side on large screens, top on small */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center"
                  }}
                >
                  <Typography
                    variant="bodyMedium"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 'bold'
                    }}>
                    {DisplayDate(ToDayJS(eventsOnDate[0].day.datetime as Timestamp).toDate(), timezone)}
                  </Typography>
                  {isGolfEvent && (
                    <IconButton
                      size="small"
                      onClick={() => handleAddEventToDate(date)}
                      title={`Add another event to ${DisplayDate(ToDayJS(eventsOnDate[0].day.datetime as Timestamp).toDate(), timezone, 'MMMM D')}`}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>

                {/* Course/time rows - takes remaining space on large screens */}
                <Stack spacing={2} sx={{ flex: isLargeScreen ? 1 : undefined, maxWidth: isLargeScreen ? '40em' : undefined }}>
              {eventsOnDate.map(({ day, index }) => {
                const isConfirmingDelete = confirmDeleteIndex === index;
                const isExpanded = expandedRows.has(index);
                // Explicit per-day override takes precedence over the event's overall Golf/Social
                // setting — lets one day of a golf event be a social-only day (banquet, etc.) and
                // vice versa. Defaults to the event's setting (Round) when the day hasn't been
                // overridden yet.
                const dayIsGolf = day.isGolf ?? isGolfEvent;
                return (
                  <Box key={index}>
                    <Stack spacing={1} sx={{size: "100%"}}>
                      {/* Main row: Course/Location, Start time, MoreVert */}
                      <Stack
                        direction="row"
                        spacing={{ xs: 1, sm: 2 }}
                        sx={{
                          alignItems: "center",
                          justifyContent: "flex-end"
                        }}>
                        {dayIsGolf ? (
                          <Box sx={{ minWidth: '8em', maxWidth: '40em', flex: 1}}>
                            <CourseSelector
                              value={day.courseId ?? ''}
                              shortName={true}
                              handleChange={(course) => handleCourseChange(index, course)}
                            />
                          </Box>
                        ) : (
                          <TextField
                            id="socialLocation"
                            label="Location"
                            value={day.socialLocation || ''}
                            onChange={({ target }) => {
                              const newDays = [...updatedDays];
                              newDays[index].socialLocation = target.value;
                              onChange(newDays, coursePairs);
                            }}
                            sx={{ flex: 1, minWidth: '8em' }}
                          />
                        )}
                        <TimePicker
                          label="Start time"
                          value={ToDayJS(day.datetime as Timestamp, timezone)}
                          onChange={(target) => {
                            if (target != null && target.isValid()) {
                              const newDays = [...updatedDays];
                              newDays[index].datetime = Timestamp.fromDate(target.toDate());
                              onChange(newDays, coursePairs);
                            }
                          }}
                          format="h:mm A"
                          sx={{ width: '9rem' }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpanded(index)}
                          title={isExpanded ? 'Hide options' : 'Show more options'}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <MoreVertIcon />}
                        </IconButton>
                      </Stack>

                      {/* Expanded row: Duration, Shotgun, Practice, Delete (if multiple events) */}
                      {isExpanded && (
                        <Stack
                          direction="row"
                          sx={{
                            alignItems: "center",
                            pt: 1,
                            gap: { xs: 1, sm: 2 },
                            flexWrap: 'wrap'
                          }}>
                          {isConfirmingDelete ? (
                            <>
                              <Typography variant="body2" sx={{
                                color: "text.secondary"
                              }}>
                                {hasDownstreamScores(index)
                                  ? 'Delete this time? Scores already exist for this day or a later one — removing it may shift them onto the wrong day.'
                                  : 'Delete this time?'}
                              </Typography>
                              <Button size="small" color="error" onClick={() => handleDeleteEventDay(index)}>
                                Confirm
                              </Button>
                              <Button size="small" variant="outlined" onClick={handleCancelDelete}>
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">Round / Social</Typography>
                                <ToggleButtonGroup
                                  exclusive
                                  size="small"
                                  value={dayIsGolf ? 'round' : 'social'}
                                  onChange={(_, val) => {
                                    if (val === null) return;
                                    const newDays = [...updatedDays];
                                    const goingGolf = val === 'round';
                                    newDays[index] = { ...newDays[index], isGolf: goingGolf };
                                    if (!goingGolf) {
                                      // Golf-only flags aren't shown once a day is Social, but they
                                      // don't stop being read elsewhere just because they're
                                      // hidden here — e.g. a day cloned from a Practice round and
                                      // then switched to Social would otherwise silently keep
                                      // isPractice: true and still be labeled "Practice" on the
                                      // event card. Clear them so Social is a clean slate.
                                      delete newDays[index].isPractice;
                                      delete newDays[index].isShotgun;
                                      delete newDays[index].numHoles;
                                      delete newDays[index].which9;
                                    }
                                    onChange(newDays, coursePairs);
                                  }}
                                >
                                  <ToggleButton value="round">Round</ToggleButton>
                                  <ToggleButton value="social">Social</ToggleButton>
                                </ToggleButtonGroup>
                              </Stack>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimeField
                                  label="Duration"
                                  value={durationToDayjs(day.duration ?? 0)}
                                  onChange={(newValue) => {
                                    if (newValue) {
                                      const newDays = [...updatedDays];
                                      newDays[index].duration = dayjsToDuration(newValue);
                                      onChange(newDays, coursePairs);
                                    }
                                  }}
                                  format="HH:mm"
                                  size="small"
                                  sx={{ width: '7rem' }}
                                />
                              </LocalizationProvider>
                              {!dayIsGolf && (
                                <TextField
                                  id="socialName"
                                  label="Name"
                                  placeholder="Social"
                                  value={day.socialName || ''}
                                  onChange={({ target }) => {
                                    const newDays = [...updatedDays];
                                    newDays[index].socialName = target.value;
                                    onChange(newDays, coursePairs);
                                  }}
                                  size="small"
                                  sx={{ minWidth: '10em' }}
                                />
                              )}
                              {dayIsGolf && (
                                <>
                                  <EventDayHolesToggle
                                    numHoles={day.numHoles}
                                    which9={day.which9}
                                    onChange={(numHoles, which9) => {
                                      const newDays = [...updatedDays];
                                      newDays[index].numHoles = numHoles;
                                      if (which9 === undefined) delete newDays[index].which9;
                                      else newDays[index].which9 = which9;
                                      onChange(newDays, coursePairs);
                                    }}
                                  />
                                  <Stack direction="row" sx={{
                                    alignItems: "center"
                                  }}>
                                    <FormGroup>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            checked={day.isShotgun ?? false}
                                            name="shotgun"
                                            onChange={({ target }) => {
                                              const newDays = [...updatedDays];
                                              newDays[index].isShotgun = target.checked;
                                              onChange(newDays, coursePairs);
                                            }}
                                          />
                                        }
                                        label="Shotgun"
                                        labelPlacement="top"
                                      />
                                    </FormGroup>
                                    <FormGroup>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            checked={day.isPractice ?? false}
                                            name="practice"
                                            onChange={({ target }) => {
                                              const newDays = [...updatedDays];
                                              newDays[index].isPractice = target.checked;
                                              onChange(newDays, coursePairs);
                                            }}
                                          />
                                        }
                                        label="Practice"
                                        labelPlacement="top"
                                      />
                                    </FormGroup>
                                    {day.isShotgun ? (
                                      <EventDayNumberField
                                        label="Starting hole"
                                        value={resolveShotgunStartHole(day)}
                                        min={day.numHoles === 9 && day.which9 === 'back' ? 10 : 1}
                                        max={day.numHoles === 9 && day.which9 !== 'back' ? 9 : 18}
                                        onCommit={(hole) => {
                                          // The round's first hole is the default, so it isn't stored
                                          const newDays = [...updatedDays];
                                          const next: EventDay = { ...newDays[index], shotgunStartHole: hole };
                                          if (hole === (day.numHoles === 9 && day.which9 === 'back' ? 10 : 1)) delete next.shotgunStartHole;
                                          newDays[index] = next;
                                          onChange(newDays, coursePairs);
                                        }}
                                      />
                                    ) : (
                                      <EventDayNumberField
                                        label="Minutes apart"
                                        value={resolveTeeTimeSpacing(day, coursePairs)}
                                        min={1}
                                        max={60}
                                        onCommit={(minutes) => {
                                          // Matching the course's spacing isn't stored, so the day follows the course
                                          const newDays = [...updatedDays];
                                          const next: EventDay = { ...newDays[index], teeTimeSpacing: minutes };
                                          if (minutes === resolveCourseTeeTimeSpacing(day, coursePairs)) delete next.teeTimeSpacing;
                                          newDays[index] = next;
                                          onChange(newDays, coursePairs);
                                        }}
                                      />
                                    )}
                                  </Stack>
                                </>
                              )}
                              {eventsOnDate.length > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => setConfirmDeleteIndex(index)}
                                  title="Delete this event time"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                );
              })}
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  }
}

/*
              {dayIsGolf && courses && courses.length > 0 && (
                <TextField
                  id="location"
                  label="Location"
                  autoFocus={false}
                  select
                  aria-label="location"
                  aria-required="false"
                  value={days[i].courseId ? days[i].courseId : ""}
                  fullWidth
                  onChange={({ target }) => {
                    const newDays = [...days];
                    newDays[i].courseId = target.value;
                    onChange(newDays);
                  }}
                >
                  {CourseMenu()}
                </TextField>
              )}
*/
